import React, { useEffect, useState } from "react";
import { Button, Image, Spin } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useParams } from "react-router-dom";
import { IAssignment, ISubmission } from "interfaces";
import { Pagination } from './pagination';
import './viewer.css';

interface SubmissionProps {
  assignment: IAssignment;
  submission: ISubmission | null;
}

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export const Submission: React.FC<SubmissionProps> = ({
  assignment,
  submission,
}) => {
  const { id, course_id } = useParams();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(true);
  const [filename, setFileName] = useState<string>();
  const [loadingImage, setLoadingImage] = useState<boolean>(true);
  const preview_url = `https://saintsburg.instructure.com/courses/${course_id}/assignments/${assignment?.id}/submissions/${id}?preview=1`;
  const download_url = submission?.attachments?.[0]?.url ?? "";
  const attachment_url = submission?.attachments?.[0]?.url ?? "";

  useEffect(() => {
    if (submission?.attachments?.[0]?.filename) {
      setFileName(submission?.attachments?.[0]?.filename)
    }
  }, [submission])

  const isPDF = filename?.toLowerCase().endsWith(".pdf");
  const isImage = ["jpg", "jpeg", "png", "gif"].some(ext => filename?.toLowerCase().endsWith(ext));
  const isDiscussion = submission?.submission_type === "discussion_topic";

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoadingPdf(false);
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  const handleImageLoad = () => {
    setLoadingImage(false);
  };

  const handleImageError = () => {
    setLoadingImage(false);
  };

  const hasSubmissions = !!((submission && submission.attachments?.length > 0) || isDiscussion);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          href={preview_url}
          target="_blank"
          rel="noreferrer"
          icon={<EyeOutlined />}
          style={{ marginRight: 12 }}
          disabled={!hasSubmissions}
        >
          View
        </Button>
        <a href={download_url} download>
          <Button type="default" icon={<DownloadOutlined />} disabled={!hasSubmissions}>
            Download
          </Button>
        </a>
      </div>
      <div style={{ marginTop: 16, textAlign: "center" }}>
        {isPDF ? (
          <div style={{ position: "relative", overflow: "hidden" }}>
            {loadingPdf && <Spin />}
            <Document
              file={attachment_url}
              onLoadSuccess={onDocumentLoadSuccess}
              renderMode="canvas"
              options={{ workerSrc: "/pdf.worker.js" }}
              className="pdf-document"
            >
              <Page
                pageNumber={pageNumber}
                width={500}
                renderTextLayer={false}
              />
            </Document>
            <Pagination
              currentPage={pageNumber}
              totalPages={numPages}
              onPageChange={handlePageChange}
            />
          </div>
        ) : isImage ? (
          <div style={{ textAlign: "center" }}>
            {loadingImage && (
              <>
                <Spin />
                <p>Loading Image...</p>
              </>
            )}
            <Image
              src={attachment_url}
              preview={false}
              style={{ maxWidth: "100%", maxHeight: "500px" }}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
        ) : isDiscussion ? (
          <p></p>
        ) : (
          <p>No Submission</p>
        )}
      </div>
    </div>
  );
};
